import { useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";

import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";

import type { CrmWhatsappAccountConfig } from "../../statistics/types";

type CrmWhatsAppAccountConfigFormValues = {
  wabaPhoneNumber: string;
  whatsappPhoneNumber: string;
  metaBusinessAccountId: string;
  connectionStatusLabel: string;
};

const DEFAULT_FORM_VALUES: CrmWhatsAppAccountConfigFormValues = {
  wabaPhoneNumber: "",
  whatsappPhoneNumber: "",
  metaBusinessAccountId: "",
  connectionStatusLabel: "",
};

export interface UseCrmWhatsAppAccountConfigFormParams {
  open: boolean;
  config: CrmWhatsappAccountConfig | null;
  onSubmit: (input: CrmWhatsappAccountConfig) => Promise<boolean>;
  /**
   * Callback opcional executado quando `onSubmit` retornar `true`.
   * A camada de orquestração (ex.: Dialog) pode usar isso para fechar/atualizar UI.
   */
  onSaved?: () => void;
}

export interface UseCrmWhatsAppAccountConfigFormResult {
  register: ReturnType<
    typeof useForm<CrmWhatsAppAccountConfigFormValues>
  >["register"];
  handleSubmit: (
    event: React.FormEvent<HTMLFormElement>,
  ) => void;
  formErrors: ReturnType<
    typeof useForm<CrmWhatsAppAccountConfigFormValues>
  >["formState"]["errors"];

  isSubmitting: boolean;
  hasSubmitError: boolean;

  /**
   * Dispara validação do formulário e executa o submit.
   * O callback `onSaved` será chamado internamente quando `onSubmit` retornar `true`.
   */
  submit: () => Promise<void>;
}

function toFormValues(
  input: CrmWhatsappAccountConfig,
): CrmWhatsAppAccountConfigFormValues {
  return {
    wabaPhoneNumber: input.wabaPhoneNumber,
    whatsappPhoneNumber: input.whatsappPhoneNumber,
    metaBusinessAccountId: input.metaBusinessAccountId,
    connectionStatusLabel: input.connectionStatusLabel,
  };
}

function toConfig(
  values: CrmWhatsAppAccountConfigFormValues,
): CrmWhatsappAccountConfig {
  return {
    wabaPhoneNumber: values.wabaPhoneNumber,
    whatsappPhoneNumber: values.whatsappPhoneNumber,
    metaBusinessAccountId: values.metaBusinessAccountId,
    connectionStatusLabel: values.connectionStatusLabel,
  };
}

export function useCrmWhatsAppAccountConfigForm(
  params: UseCrmWhatsAppAccountConfigFormParams,
): UseCrmWhatsAppAccountConfigFormResult {
  const { open, config, onSubmit, onSaved } = params;

  const {
    register,
    handleSubmit: rhfHandleSubmit,
    reset,
    trigger,
    getValues,
    formState: { errors },
  } = useForm<CrmWhatsAppAccountConfigFormValues>({
    mode: "onBlur",
    defaultValues: DEFAULT_FORM_VALUES,
  });

  const {
    run: runSubmitTask,
    loading: isSubmitting,
    status: submitStatus,
  } = useAsyncTask<
    [CrmWhatsAppAccountConfigFormValues],
    boolean,
    unknown
  >({
    task: async (values) => {
      const input = toConfig(values);
      return onSubmit(input);
    },
  });

  const executeSubmit = useCallback(
    async (values: CrmWhatsAppAccountConfigFormValues) => {
      try {
        const wasSaved = await runSubmitTask(values);
        if (!wasSaved) return;
        onSaved?.();
      } catch {
        // O estado de erro/loading é controlado por `useAsyncTask`.
      }
    },
    [onSaved, runSubmitTask],
  );

  const handleSubmit = rhfHandleSubmit(async (values) => {
    await executeSubmit(values);
  });

  const submit = useCallback(async () => {
    const isValid = await trigger();
    if (!isValid) return;

    await executeSubmit(getValues());
  }, [executeSubmit, getValues, trigger]);

  useEffect(() => {
    if (!open) return;

    if (config) {
      reset(toFormValues(config));
      return;
    }

    reset(DEFAULT_FORM_VALUES);
  }, [config, open, reset]);

  return {
    register,
    handleSubmit,
    formErrors: errors,
    isSubmitting,
    hasSubmitError: submitStatus === "error",
    submit,
  };
}

